
import { Component, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTable from "@/components/vTable.vue";
import {downloadURIWithStatus, mapNutritions} from "@/utils/Helpers";
import { Dish } from "@/models/Dishes/Dish";
import vModal from "@/components/vModal.vue";
import vRecipeListing from "@/components/vRecipeListing.vue";
import vRecipeDetails from "@/components/vRecipeDetails.vue";
import vAuditLogModal from "@/components/vAuditLogModal.vue";
import { ItemCompact } from "@/models/ItemCompact";
import { CaloriesCalculationMethod } from "@/models/Recipes/CaloriesCalculationMethod";
import { DailyValueCalculationMethod } from "@/models/Recipes/DailyValueCalculationMethod";
import vCopyRecipeModal from "@/components/vCopyRecipeModal.vue";
import vSingleNutrientReportModal from "@/components/reports/vSingleNutrientReportModal.vue";
import vProteinQualityReport from "@/components/reports/vProteinQualityReport.vue";
import vBreakdownReportModal from "@/components/vBreakdownReportModal.vue";
import VAminoAcidScoreReport from "@/components/reports/vAminoAcidScoreReport.vue";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vRecipeDeleteConfirmationModal from "@/components/vRecipeDeleteConfirmationModal.vue";
import vMultiColumnReportModal from "@/components/vMultiColumnReportModal.vue";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import {AccountState} from "@/store/modules/AccountModule";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import vPreLoader from "@/components/vPreLoader.vue";
import vGenerateReportModal from "@/components/vGenerateReportModal.vue";
import ListRequest, { OrderType } from "@/models/ListRequest";
import { REPORT_NOTIFICATION_SHOW } from "@/store/ActionTypes";
import {
  GenerateReportOption,
} from "@/models/Reports/GenerateReport";

interface DishesState {
  dish: Dish;
  selectedDish: ItemCompact;
}

@Component({
  components: {
    VUpsellModal,
    vMultiColumnReportModal,
    vBreadcrumbs,
    vTable,
    vModal,
    vRecipeListing,
    vRecipeDetails,
    vAuditLogModal,
    vCopyRecipeModal,
    vBreakdownReportModal,
    vSingleNutrientReportModal,
    vProteinQualityReport,
    VAminoAcidScoreReport,
    vRecipeDeleteConfirmationModal,
    vPreLoader,
    vGenerateReportModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class Recipes extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  dishesState: DishesState = {
    dish: this.createDefaultDish(),
    selectedDish: {
      id: 0,
      name: "",
      createdOn: null,
      updatedOn: null,
      createdUserName: "",
      updatedUserName: ""
    }
  };

  // Modal options
  showDeleteRecipeModal = false;
  showCopyRecipeModal = false;
  showBreakdownReportModal = false;
  showProteinQualityReportModal = false;
  showAminoAcidScoreReportModal = false;
  showSingleNutrientReportModal = false;
  showMultiColumnReportModal = false;
  showGenerateReportModal = false;

  recipeForCopy: Dish = this.createDefaultDish();
  checkedRecipesForDelete: number[] = [];
  funcHandler :any;

  // List of dish ids to be deleted
  removeList: number[] = [];
  checked: number[] = [];

  selectedBulkReportType = "export";
  selectedBulkReportCountry = "0";
  includeAllergensInReport = false;
  isGeneratingReport = false;
  combinedRecipeCount: number | null = null;

  showTrialUpsellModal: boolean = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  isDishDetailsLoading = false;
  lastDishDetailsRequestId = 0;

  openTrialUpsellModal(message: string = this.defaultTrialUpsellMessage) {
    this.trialUpsellMessage = message;
    this.showTrialUpsellModal = true;
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  async handleAddDishClick() {
    await this.$router.push({ name: "dishesAdd" });
  }

  get reportTypeOptions(): GenerateReportOption[] {
    return [
      {
        value: "export",
        name: this.translations.get("dish_export_to_excel"),
        description: this.translations.get("reports_dishes_export_option_description"),
      },
    ];
  }

  get countryOptions(): GenerateReportOption[] {
    return [
      { value: "0", name: this.translations.get("canada") },
      { value: "1", name: this.translations.get("country_us") },
      { value: "2", name: this.translations.get("country_uk") },
    ];
  }

  get isReportActionDisabled() {
    return false;
  }

  get combinedRecipeCountSummary() {
    if (this.combinedRecipeCount === null) {
      return this.translations.get("reports_loading_combined_recipe_count");
    }

    return this.combinedRecipeCount === 1
      ? this.translations.get("reports_combined_recipe_count_single")
      : this.translations.get("reports_combined_recipe_count_plural").replace("{count}", String(this.combinedRecipeCount));
  }

  get isGenerateReportSubmitDisabled() {
    return this.combinedRecipeCount === 0;
  }

  changeBulkReportType(value: string) {
    this.selectedBulkReportType = value;
  }

  changeBulkReportCountry(value: string) {
    this.selectedBulkReportCountry = value;
  }

  changeIncludeAllergens(value: boolean) {
    this.includeAllergensInReport = value;
  }

  print() {
    window.print();
  }

  get isTrial() {
    return this.$store.getters.getUserDetails.isTrial;
  }

  openReport(id: number) {
    if (this.accountState.userDetails.isTrial) {
      this.openTrialUpsellModal();
      return;
    }

    switch (id) {
    case 0:
      this.openRecipeReportModal();
      break;
    case 1:
      this.openProteinQualityReportModal();
      break;
    case 2:
      this.openAminoAcidScoreReportModal();
      break;
    case 3:
      this.openSingleNutrientReportModal();
      break;
    case 4:
      this.openMultiColumnReportModal();
      break;
    }
  }

  openGenerateReportModal(defaultReportType = "export") {
    this.selectedBulkReportType = defaultReportType;
    this.selectedBulkReportCountry = "0";
    this.includeAllergensInReport = false;
    this.showGenerateReportModal = true;
    void this.loadCombinedRecipeCount();
  }

  closeGenerateReportModal() {
    this.showGenerateReportModal = false;
  }

  async handleGenerateReport() {
    this.showGenerateReportModal = false;
    this.isGeneratingReport = true;

    try {
      if (await downloadURIWithStatus(
        this.exportToExcelURL,
        `Combined dishes report (${this.getSelectedCountryLabel()})`
      )) {
        this.$store.dispatch(REPORT_NOTIFICATION_SHOW, {
          message: "Your combined dishes report download is now available.",
        });
      }
    }
    finally {
      this.isGeneratingReport = false;
    }
  }

  // Open modal and view recipe report
  openRecipeReportModal() {
    this.showBreakdownReportModal = true;
  }

  openProteinQualityReportModal() {
    this.showProteinQualityReportModal = true;
  }

  hideProteinQualityReportModal() {
    this.showProteinQualityReportModal = false;
  }

  openAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = true;
  }

  hideAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = false;
  }

  openSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = true;
  }

  hideSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = false;
  }

  openMultiColumnReportModal(){
    this.showMultiColumnReportModal = true;
  }

  hideMultiColumnReportModal(){
    this.showMultiColumnReportModal = false;
  }

  auditLogModal = {
    isOpen: false,
  };

  onAuditLogClick(dish: ItemCompact) {
    this.auditLogModal.isOpen = true;
    this.dishesState.selectedDish = { ...dish };
  }

  openCopyModal() {
    this.showCopyRecipeModal = true;
    RecipesRepository.getRecipeDetails(this.dishesState.dish.id, true).then(
      (response) => {
        this.recipeForCopy = response.data;
        this.recipeForCopy.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
      }
    );
  }

  openDeleteModal(recipeId: number[], handler: (recipeIds: number[]) => void): void {
    this.checkedRecipesForDelete = recipeId;
    this.showDeleteRecipeModal = true;
    this.funcHandler = handler;
  }

  createDefaultDish(): Dish {
    return {
      id: 0,
      name: '',
      description: '',
      nutritionAnalysis: null,
      createdOn: null,
      updatedOn: null,
      createdUserName: null,
      updatedUserName: null,

      servings: 1,
      recipeCategory: null,
      instructions: null,

      ingredients: [],
      childRecipes: [],
      caloriesCalculationMethod: CaloriesCalculationMethod.Summation,
      dailyValueCalculationMethod: DailyValueCalculationMethod.Rounded,
      proteinDvCalculationMethod: 0,
      pdcaasScore: 1,
      nutritionCountry: { id: 0, name: 'Canada' },
      referenceAmount: 0,
      recipeProductType: {
        id: 0,
        name: "",
        productType: 0,
        countryId: 0,
        createdOn: null
      },
      productTypeId: 0,
    };
  }

  /**
   * Dynamically return the url for export depending on the current environment
   */
  get exportToExcelURL() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Dishes/ExportDishes?country=${this.selectedBulkReportCountry}&includeAllergens=${this.includeAllergensInReport}`;
  }

  async loadCombinedRecipeCount() {
    this.combinedRecipeCount = null;

    try {
      const listRequest = new ListRequest({
        page: 1,
        pageSize: 5000,
        filters: [],
        orders: [{ field: "Name", orderType: OrderType.Asc }],
      });

      const response = await RecipesRepository.listReducedRecipes(listRequest, false);
      this.combinedRecipeCount = Array.isArray(response.data?.result)
        ? response.data.result.length
        : 0;
    }
    catch {
      this.combinedRecipeCount = 0;
    }
  }

  getSelectedCountryLabel() {
    switch (this.selectedBulkReportCountry) {
    case "0":
      return "Canada";
    case "1":
      return "USA";
    case "2":
      return "UK";
    default:
      return "Canada";
    }
  }

  /**
   * Click handler for showing the selected recipe
   * */
  async chooseDish(dishId: number) {
    this.isDishDetailsLoading = true;
    const requestId = ++this.lastDishDetailsRequestId;

    try {
      const response = await RecipesRepository.getRecipeDetails(dishId, true);

      if (requestId !== this.lastDishDetailsRequestId) {
        return;
      }

      this.dishesState.dish = { ...(response.data as Dish) };
      this.dishesState.dish.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
    } catch (error: any) {
      if (requestId !== this.lastDishDetailsRequestId) {
        return;
      }

      if (error.response) {
        this.$snotify.error(error.response.data?.errorMessages?.[0]?.userVisibleMessage || error.response.data?.message);
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    } finally {
      if (requestId === this.lastDishDetailsRequestId) {
        this.isDishDetailsLoading = false;
      }
    }
  }

  get translations() {
    return this.localizationState.translations;
  }

  /**
   * Method which requests delete recipes to API and shows toastr message
   */
  removeRecipes() {
    this.funcHandler(this.checkedRecipesForDelete);
    this.showDeleteRecipeModal = false;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "dishes", "ingredients"],
      })
      .then(() => {
        document.title = (
          this.translations.get("dish_title") + " " + this.translations.get("meta_title_postfix")
        );
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  saveCopiedRecipe(newRecipeName : string) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_loading"),
      () =>
        new Promise((success, reject) => {
          RecipesRepository.copyRecipe(this.recipeForCopy.id, newRecipeName)
            .then((response) => {
              this.showCopyRecipeModal = false;
              this.$router.push({
                name: "dishesEdit",
                params: { id: `${response.data.id}` },
              });
              success({
                body: this.translations.get("rec_copy_combined_recipe_success"),
              });
            })
            .catch((error: ErrorResponse) => {
              this.showCopyRecipeModal = false;
              let message;

              if (error.response) {
                message = error.response.data.message;
              }

              else {
                message = "Unknown error while copying item.";
              }

              reject({ body: message });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }
}
export default Recipes;
